/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { css, cx, cva } from "@panda/css";
import { Box, HStack } from "@panda/jsx";
import { resolvePandaColor } from "@components/utils";
import { useTabsContext } from "./tabs.context";

export interface CosmosTabsButtonListProps {
	/** Custom CSS class name */
	className?: string;
	/** Enable horizontal scrolling with gradient fade (default: false for wrapping) */
	scrollTabs?: boolean;
	/** Child tab triggers */
	children: React.ReactNode;
	/** Background color for scroll gradient fade. Only used when scrollTabs is true.
	 * Pass the same background color as the container behind the tabs for a seamless fade effect.
	 * Accepts Panda token paths ("eds.background.elevated.low.default"), enum values
	 * ("ElevatedLowDefault"), or CSS colors ("#1a1d22"). Defaults to "eds.background.default". */
	gradientColor?: string | null;
}

const gradientStyles = cva({
	base: {
		position: "absolute",
		width: "60px",
		height: "100%",
		filter: "blur(2px)",
		pointerEvents: "none",
		transition: "opacity .1s ease",
		top: 0,
	},
	variants: {
		enable: {
			true: {
				opacity: "1",
			},
			false: {
				opacity: "0",
			},
		},
	},
});

/**
 * Container for tab buttons. Expects to wrap one or more Tabs.Button components.
 */
export const CosmosTabsButtonList = ({
	className,
	scrollTabs = false,
	gradientColor = "eds.background.default",
	children,
}: CosmosTabsButtonListProps): React.JSX.Element => {
	const { setHasTabsList } = useTabsContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const [showLeft, setShowLeft] = useState(false);
	const [showRight, setShowRight] = useState(false);

	// Register this TabsList when mounted (for accessibility)
	useEffect(() => {
		setHasTabsList?.(true);
		return () => setHasTabsList?.(false);
	}, [setHasTabsList]);

	// Styles for scrollable horizontal layout
	const scrollStyles = css({
		overflowX: "auto",
		maxWidth: "100%",
		"&::-webkit-scrollbar": {
			zIndex: "1",
		},
		"&::-webkit-scrollbar-track": {
			bg: "eds.background.elevated.low.default",
			borderBlock: "token(sizes.eds.dimension.1) solid token(colors.eds.border.subtle)",
		},
		"&::-webkit-scrollbar-thumb": {
			bg: "transparent",
			boxShadow: "0 0 0 6px token(colors.eds.palette.transparent.light.035) inset",
			border: "4px solid transparent",
			borderRadius: "eds.borderRadius.8",
		},
		"& > *": {
			flexShrink: 0,
		},
	});

	const wrapStyles = css({
		flexWrap: "wrap",
	});

	const containerStyles = scrollTabs ? scrollStyles : wrapStyles;

	const formattedColor = useMemo(() => resolvePandaColor(gradientColor), [gradientColor]);

	// Check scroll position to show/hide gradients
	const checkScroll = useCallback((): void => {
		if (!scrollTabs) return;
		const el = containerRef.current;
		if (!el) return;

		// Horizontal scrolling
		if (el.scrollWidth > el.clientWidth) {
			setShowLeft(Math.floor(el.scrollLeft) > 0);
			setShowRight(Math.ceil(el.scrollLeft) < el.scrollWidth - el.clientWidth);
		} else {
			setShowLeft(false);
			setShowRight(false);
		}
	}, [scrollTabs]);

	// Set up scroll and resize listeners
	useEffect(() => {
		if (!scrollTabs) return;
		checkScroll();
		const el = containerRef.current;
		if (!el) return;

		el.addEventListener("scroll", checkScroll);
		window.addEventListener("resize", checkScroll);

		return () => {
			el.removeEventListener("scroll", checkScroll);
			window.removeEventListener("resize", checkScroll);
		};
	}, [checkScroll, scrollTabs]);

	// For scroll layout, wrap in a Box with side fadeout gradients
	if (scrollTabs) {
		return (
			<Box position="relative" maxWidth="100%">
				<HStack
					ref={containerRef}
					className={cx(containerStyles, "cosmos-tabs-list", className)}
					gap="eds.dimension.16"
					role="tablist"
					aria-orientation="horizontal"
				>
					{children}
				</HStack>
				<Box
					left="-1"
					style={{ background: `linear-gradient(to right, ${formattedColor}, transparent)` }}
					className={gradientStyles({ enable: showLeft })}
				/>
				<Box
					right="-1"
					style={{ background: `linear-gradient(to left, ${formattedColor}, transparent)` }}
					className={gradientStyles({ enable: showRight })}
				/>
			</Box>
		);
	}

	// For wrap layout, no gradients needed
	return (
		<HStack
			className={cx(containerStyles, "cosmos-tabs-list", className)}
			gap="eds.dimension.16"
			role="tablist"
			aria-orientation="horizontal"
		>
			{children}
		</HStack>
	);
};

CosmosTabsButtonList.displayName = "CosmosTabs.ButtonList";
